import { useMemo } from 'react'

import {
  matchFilterArrayIncludes, matchFilterNumber, matchFilterString, sortArrayBy,
} from '@domain-group/javascript-utils'

import { useLoadMore, useWatchEntityUpdates } from '@domain-group/react-hooks'

import useReduxAction from '@hooks/useReduxAction'
import useSelector from '@hooks/useSelector'

import type { LayoutTemplateModel, LayoutTemplateRequestOptions } from '@domain-group/realhub-backend-api-types'
import type { ModuleState } from '@redux/modules/types'

const watchEntityKeys = ['layoutTemplates']

type LayoutTemplateFilters = {
  brand_id?: string,
  hidden?: boolean,
  publication_product_id?: string,
  string?: string,
}

type UseLayoutTemplatesOptions = {
  filters?: LayoutTemplateFilters,
  performHttpRequests?: boolean,
  requestOptions?: LayoutTemplateRequestOptions,
}

function useLayoutTemplates(options: UseLayoutTemplatesOptions) {
  const { filters = {}, requestOptions } = options
  const {
    brand_id: filterBrandId,
    hidden: filterHidden,
    publication_product_id: filterPublicationProductId,
    string: filterString,
  } = filters

  const {
    updatedEntities: { layoutTemplates: layoutTemplatesUpdatedAt },
  } = useWatchEntityUpdates(watchEntityKeys)

  const { layoutTemplates } = useSelector(reduxState => reduxState.entities)

  const filteredLayoutTemplates = useMemo(() => {
    const filtered = Object.values(layoutTemplates).filter((layoutTemplate: LayoutTemplateModel) => {
      const {
        brand_id, compatible_products, hidden, id, title,
      } = layoutTemplate

      const matchId = id?.toString() === filterString
      const matchTitle = matchFilterString(title, filterString)
      const stringMatch = matchId || matchTitle
      // @ts-expect-error TS2345
      const brandMatch = matchFilterNumber(brand_id, filterBrandId)
      const hiddenMatch = filterHidden ? !!hidden : !hidden
      // @ts-expect-error
      const publicationMatch = filterPublicationProductId ? matchFilterArrayIncludes(compatible_products, Number(filterPublicationProductId)) : true

      return brandMatch && stringMatch && hiddenMatch && publicationMatch
    })

    return sortArrayBy(filtered, 'asc', 'title')
  }, [layoutTemplatesUpdatedAt, JSON.stringify(filters)])

  const filteredLayoutTemplatesCount = filteredLayoutTemplates.length
  const hasFilteredLayoutTemplates = !!filteredLayoutTemplatesCount

  const loadMorePayload = useLoadMore({
    filters,
    loadedCount: filteredLayoutTemplatesCount,
    performHttpRequests: options.performHttpRequests,
  })

  const {
    callbacks: { loadMore },
    canLoadMore,
    filtersWithOffset,
    limit,
    performHttpRequests,
  } = loadMorePayload

  const { loading: loadingLayoutTemplates } = useReduxAction(
    'layoutTemplates',
    'loadLayoutTemplates',
    {
      ...requestOptions,
      ...filtersWithOffset,
      limit,
    },
    [filtersWithOffset, performHttpRequests],
    {
      shouldPerformFn: ({ loading }: ModuleState) => performHttpRequests && !loading,
    },
  )

  return {
    callbacks: {
      loadMore,
    },
    canLoadMore,
    filteredLayoutTemplates,
    filteredLayoutTemplatesCount,
    hasFilteredLayoutTemplates,
    loading: loadingLayoutTemplates,
  }
}

export default useLayoutTemplates
